import { StyledTable } from '@/shared/ui/tables'
import React, { forwardRef, useCallback, MouseEvent } from 'react'
import {
    Box,
    Card,
    CardActions,
    CardContent,
    Grid,
    Paper,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Typography,
} from '@mui/material'
import { CardBlockProps, Dossier, ContentItem, DisplayItem } from '../types'
import { translateDisplayItem } from '../../../utils/helpers'
import DossierModalEditButton from '@/feature/ContentDossier/DossierModalEditButton'

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface CardDefaultProps extends CardBlockProps {}

export const CardDefault = forwardRef<HTMLDivElement, CardDefaultProps>(
    ({ kedo, block, data, listItems, getValue, handleContent }, ref) => {
        const renderCard = useCallback(
            (dossier: Dossier, dossierData: ContentItem[]) => {
                const isArchived = dossier.archived
                const showInlineEdit = block.settings?.inline_edit

                const onCardClick = (event: MouseEvent<HTMLDivElement>) => {
                    handleContent(event, dossier)
                }

                return (
                    <Grid
                        size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                        key={dossier.id}
                    >
                        <Card
                            onClick={onCardClick}
                            sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                cursor: 'pointer',
                                opacity: isArchived ? 0.6 : 1,
                                '&:hover': {
                                    boxShadow: 4,
                                },
                            }}
                        >
                            <CardContent sx={{ flexGrow: 1 }}>
                                <Typography
                                    variant="h6"
                                    component="h2"
                                    gutterBottom
                                >
                                    {dossier.summary}
                                </Typography>
                                <TableContainer component={Paper}>
                                    <StyledTable size="small" striped>
                                        <TableBody>
                                            {listItems.map((listItem) => (
                                                <TableRow key={listItem.id}>
                                                    <TableCell
                                                        component="th"
                                                        sx={{
                                                            fontWeight: 600,
                                                            width: '40%',
                                                        }}
                                                    >
                                                        {translateDisplayItem(
                                                            listItem,
                                                            kedo
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {getValue(
                                                            listItem,
                                                            dossierData,
                                                            dossier
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </StyledTable>
                                </TableContainer>
                            </CardContent>
                            {showInlineEdit && (
                                <CardActions>
                                    <DossierModalEditButton
                                        dossierId={dossier.id}
                                        kedo={kedo}
                                    />
                                </CardActions>
                            )}
                        </Card>
                    </Grid>
                )
            },
            [
                block.settings?.inline_edit,
                kedo,
                listItems,
                getValue,
                handleContent,
            ]
        )

        return (
            <Box ref={ref}>
                <Grid container spacing={2}>
                    {data.dossiers.map((dossier) => {
                        const dossierData = data.contents.filter(
                            (item) => item.id === dossier.id
                        )
                        return renderCard(dossier, dossierData)
                    })}
                </Grid>
            </Box>
        )
    }
)

CardDefault.displayName = 'CardDefault'
